import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * @param {string} title
 * @param {'mafia-boss'|'bikers'|'millionaire'} niche
 * @param {string} [context]
 * @param {{ channelId?: string, voiceOverConfigId?: string, autoUploadToChannel?: boolean }} [options]
 */
export async function createScript(title, niche = 'bikers', context, options = {}) {
  const body = { title, niche, context: context || '' };
  if (context && String(context).trim()) body.context = String(context).trim();
  if (options.thumbnail !== undefined) body.thumbnail = options.thumbnail;
  if (options.channelId) body.channelId = options.channelId;
  if (options.voiceOverConfigId) body.voiceOverConfigId = options.voiceOverConfigId;
  body.autoUploadToChannel = options.autoUploadToChannel === true;
  const { data } = await axios.post(`${API_BASE_URL}/aiStoriesScript/create`, body, {
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
  });
  return data;
}

export async function updateScript(id, data) {
  const { data: res } = await axios.put(`${API_BASE_URL}/aiStoriesScript/${id}`, data, {
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
  });
  return res;
}

/** Save pasted script (editors+); sets status completed on the server. */
export async function saveScriptPasted(id, script) {
  const { data: res } = await axios.put(
    `${API_BASE_URL}/aiStoriesScript/${id}/script`,
    { script },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return res;
}

export async function deleteScript(id) {
  const { data } = await axios.delete(`${API_BASE_URL}/aiStoriesScript/${id}`, {
    headers: authHeaders(),
  });
  return data;
}

export async function generateVoiceOver(id, body = {}) {
  const { data } = await axios.post(
    `${API_BASE_URL}/aiStoriesScript/${id}/generate-voice-over`,
    body,
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return data;
}

export async function requestVoiceoverPresignedUrl(id, { fileName, fileSize, contentType }) {
  const { data } = await axios.post(
    `${API_BASE_URL}/aiStoriesScript/${id}/voiceover-presigned-url`,
    { fileName, fileSize, contentType: contentType || 'audio/mpeg' },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return data;
}

export async function completeVoiceoverUpload(id, { objectName, fileSize, duration }) {
  const { data } = await axios.post(
    `${API_BASE_URL}/aiStoriesScript/${id}/voiceover-complete`,
    { objectName, fileSize, duration },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return data;
}

export async function renderScriptVideo(id) {
  const { data } = await axios.post(
    `${API_BASE_URL}/aiStoriesScript/${id}/render`,
    {},
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return data;
}

export async function getPromptSystems() {
  const { data } = await axios.get(`${API_BASE_URL}/ai-stories/prompt-system`, {
    headers: authHeaders(),
  });
  return data;
}

export async function getPromptSystem(niche) {
  const { data } = await axios.get(`${API_BASE_URL}/ai-stories/prompt-system/${niche}`, {
    headers: authHeaders(),
  });
  return data;
}

export async function updatePromptSystem(niche, body) {
  const { data } = await axios.put(
    `${API_BASE_URL}/ai-stories/prompt-system/${niche}`,
    body,
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return data;
}

export async function createPromptSystem(body) {
  const { data } = await axios.post(
    `${API_BASE_URL}/ai-stories/prompt-system`,
    body,
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return data;
}

export async function fetchScript(id) {
  const { data } = await axios.get(`${API_BASE_URL}/aiStoriesScript/${id}`, {
    headers: authHeaders(),
  });
  return data;
}

export async function listScripts({ page = 1, limit = 10, search = '', channelId } = {}) {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  if (search && search.trim()) params.set('search', search.trim());
  if (channelId && String(channelId).trim()) params.set('channelId', String(channelId).trim());
  const { data } = await axios.get(`${API_BASE_URL}/aiStoriesScript?${params.toString()}`, {
    headers: authHeaders(),
  });
  return data;
}

const SCRIPT_POLL_INTERVAL_MS = 3000;

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Queues full script generation with NovelCrafter-style chapter writing and
 * resolves when the script is written. The server writes it in a background
 * job, so closing the page or restarting the server does not stop it.
 * When plot is empty, the backend generates it as the first step of the run.
 * @param {string} [plot]
 * @param {string} title
 * @param {'mafia-boss'|'bikers'|'millionaire'} niche
 * @param {string} scriptId
 * @param {(stepId: string, status?: 'start'|'complete', meta?: { stepNumber?: number, totalSteps?: number }) => void} [onProgress]
 */
export async function selectPlotAndGenerate(plot, title, niche, scriptId, onProgress) {
  try {
    await axios.post(
      `${API_BASE_URL}/aiStoriesScript`,
      { ...(plot ? { plot } : {}), title, niche, scriptId },
      { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
    );
  } catch (err) {
    const data = err.response?.data || {};
    throw new Error(data.message || data.error || err.message);
  }

  let reportedStep = null;
  for (;;) {
    await wait(SCRIPT_POLL_INTERVAL_MS);
    let script;
    try {
      ({ script } = await fetchScript(scriptId));
    } catch (err) {
      if (err.response?.status === 404) throw new Error('Script not found');
      // The server may be restarting; the job continues, so keep checking.
      continue;
    }
    const { currentStep, stepNumber, totalSteps } = script.progress || {};
    if (currentStep && currentStep !== reportedStep) {
      reportedStep = currentStep;
      onProgress?.(currentStep, 'start', { stepNumber, totalSteps });
    }
    if (script.status === 'completed') {
      return { success: true, scriptId, script: script.script };
    }
    if (script.status === 'script_failed') {
      throw new Error(script.scriptGenerationError || 'Script generation failed');
    }
  }
}

export { API_BASE_URL };
